import { useState } from 'react';
import { useKinship } from '../../api/kinship';
import { useSearch } from '../../api/search';
import { Users, ChevronRight, Search } from 'lucide-react';
import type { SearchResult } from '../../types/api';

interface PersonSelector {
    id: string;
    name: string;
}

export function KinshipTool() {
    const [personA, setPersonA] = useState<PersonSelector | null>(null);
    const [personB, setPersonB] = useState<PersonSelector | null>(null);
    const [searchQueryA, setSearchQueryA] = useState('');
    const [searchQueryB, setSearchQueryB] = useState('');
    const [showDropdownA, setShowDropdownA] = useState(false);
    const [showDropdownB, setShowDropdownB] = useState(false);

    const searchA = useSearch(searchQueryA, 5, 0);
    const searchB = useSearch(searchQueryB, 5, 0);

    const { data: kinshipResult, isLoading, error } = useKinship(
        personA?.id || null,
        personB?.id || null
    );

    const handleSelectA = (result: SearchResult) => {
        setPersonA({ id: result.id, name: result.fullName });
        setSearchQueryA('');
        setShowDropdownA(false);
    };

    const handleSelectB = (result: SearchResult) => {
        setPersonB({ id: result.id, name: result.fullName });
        setSearchQueryB('');
        setShowDropdownB(false);
    };

    return (
        <div className="card">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Users className="w-6 h-6 text-primary-600" />
                Xác định quan hệ họ hàng
            </h2>

            <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Person A Selector */}
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Người thứ nhất
                    </label>
                    {personA ? (
                        <div className="flex items-center gap-2 p-2 bg-primary-50 border border-primary-200 rounded-lg">
                            <div className="w-7 h-7 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 text-xs font-semibold">
                                {personA.name.charAt(0)}
                            </div>
                            <span className="flex-1 text-sm font-medium text-primary-800">{personA.name}</span>
                            <button
                                onClick={() => setPersonA(null)}
                                className="text-primary-400 hover:text-primary-600 text-xs"
                            >
                                ✕
                            </button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchQueryA}
                                onChange={(e) => {
                                    setSearchQueryA(e.target.value);
                                    setShowDropdownA(true);
                                }}
                                onFocus={() => setShowDropdownA(true)}
                                placeholder="Tìm theo tên..."
                                className="input pl-9"
                            />
                            {showDropdownA && searchQueryA && searchA.data && searchA.data.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {searchA.data.map(result => (
                                        <button
                                            key={result.id}
                                            onClick={() => handleSelectA(result)}
                                            className="w-full text-left px-3 py-2 hover:bg-primary-50 text-sm border-b border-gray-50 last:border-0"
                                        >
                                            <span className="font-medium">{result.fullName}</span>
                                            {result.birthDate && (
                                                <span className="text-gray-400 ml-2 text-xs">
                                                    ({new Date(result.birthDate).getFullYear()})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Person B Selector */}
                <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Người thứ hai
                    </label>
                    {personB ? (
                        <div className="flex items-center gap-2 p-2 bg-primary-50 border border-primary-200 rounded-lg">
                            <div className="w-7 h-7 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 text-xs font-semibold">
                                {personB.name.charAt(0)}
                            </div>
                            <span className="flex-1 text-sm font-medium text-primary-800">{personB.name}</span>
                            <button
                                onClick={() => setPersonB(null)}
                                className="text-primary-400 hover:text-primary-600 text-xs"
                            >
                                ✕
                            </button>
                        </div>
                    ) : (
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                value={searchQueryB}
                                onChange={(e) => {
                                    setSearchQueryB(e.target.value);
                                    setShowDropdownB(true);
                                }}
                                onFocus={() => setShowDropdownB(true)}
                                placeholder="Tìm theo tên..."
                                className="input pl-9"
                            />
                            {showDropdownB && searchQueryB && searchB.data && searchB.data.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                    {searchB.data.map(result => (
                                        <button
                                            key={result.id}
                                            onClick={() => handleSelectB(result)}
                                            className="w-full text-left px-3 py-2 hover:bg-primary-50 text-sm border-b border-gray-50 last:border-0"
                                        >
                                            <span className="font-medium">{result.fullName}</span>
                                            {result.birthDate && (
                                                <span className="text-gray-400 ml-2 text-xs">
                                                    ({new Date(result.birthDate).getFullYear()})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Loading */}
            {isLoading && (
                <div className="text-center py-6 text-gray-500">
                    <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                    Đang xác định quan hệ...
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                    Lỗi khi xác định quan hệ. Vui lòng thử lại.
                </div>
            )}

            {/* Result */}
            {kinshipResult && (
                <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-6">
                    <h3 className="font-semibold text-lg mb-4 text-primary-900">Kết quả</h3>

                    <div className="space-y-3">
                        <div>
                            <span className="text-sm text-gray-600">Quan hệ:</span>
                            <p className="text-lg font-bold text-gray-900">
                                {kinshipResult.relationName}
                            </p>
                        </div>

                        {kinshipResult.suggestedAddressing && (
                            <div>
                                <span className="text-sm text-gray-600">Cách gọi:</span>
                                <p className="text-xl font-bold text-primary-700">
                                    {kinshipResult.suggestedAddressing}
                                </p>
                            </div>
                        )}

                        {kinshipResult.explanation && (
                            <div>
                                <span className="text-sm text-gray-600">Mô tả:</span>
                                <p className="text-gray-700">{kinshipResult.explanation}</p>
                            </div>
                        )}

                        {kinshipResult.relationPath && kinshipResult.relationPath.length > 0 && (
                            <div>
                                <span className="text-sm text-gray-600">Đường đi:</span>
                                <div className="mt-2 flex items-center gap-1 flex-wrap">
                                    {kinshipResult.relationPath.map((personId: string, index: number) => (
                                        <div key={personId} className="flex items-center gap-1">
                                            <div className="px-3 py-1 bg-white rounded-md border border-gray-200 text-sm shadow-sm">
                                                {personId}
                                            </div>
                                            {index < kinshipResult.relationPath.length - 1 && (
                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {kinshipResult.metrics && (
                            <div className="pt-3 border-t border-primary-100">
                                <span className="text-sm text-gray-600">Chi tiết:</span>
                                <div className="mt-2 grid grid-cols-3 gap-3 text-sm">
                                    <div className="bg-white rounded-md p-2 text-center shadow-sm">
                                        <p className="text-gray-500 text-xs">Thế hệ</p>
                                        <p className="font-bold text-lg text-primary-700">{kinshipResult.metrics.generationalDiff}</p>
                                    </div>
                                    <div className="bg-white rounded-md p-2 text-center shadow-sm">
                                        <p className="text-gray-500 text-xs">Khoảng cách</p>
                                        <p className="font-bold text-lg text-primary-700">{kinshipResult.metrics.collateralDist}</p>
                                    </div>
                                    <div className="bg-white rounded-md p-2 text-center shadow-sm">
                                        <p className="text-gray-500 text-xs">Loại</p>
                                        <p className="font-bold text-sm text-primary-700">{kinshipResult.metrics.pathType}</p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!isLoading && !error && !kinshipResult && personA && personB && (
                <div className="text-center py-6 text-gray-500">
                    Chọn hai người để xác định mối quan hệ
                </div>
            )}

            {!personA || !personB ? (
                <div className="text-center py-6 text-gray-400 text-sm">
                    Tìm và chọn hai người để xem mối quan hệ
                </div>
            ) : null}
        </div>
    );
}
